import type { AssetSortField, SortDirection } from './sorting';
import apiClient from './apiClient';
import { scopeHeaders } from './organisationScope';
import type { OrgScope, WriteScope } from './organisationScope';
import type { Asset, AssetDetail, TypeWithCategoryInfo } from '@/types/asset.ts';

export interface BaseDTO {
	id: number;
	name: string;
}

/** Asset dropdown option: carries the asset's site/department so callers can
 *  filter the list by the selected site or department. */
export interface AssetBaseInfoDTO extends BaseDTO {
	siteId?: number | null;
	departmentId?: number | null;
}

/** Area dropdown option: carries the owning site so callers can narrow the list
 *  to the sites the user has selected. Null while the area is unfiled. */
export interface AreaBaseInfoDTO extends BaseDTO {
	siteId?: number | null;
}

export interface SiteWithAddressDTO {
	id: number;
	name: string;
	address?: string | null;
	city?: string | null;
	state?: string | null;
	zipCode?: string | null;
	country?: string | null;
}

export interface PagedResult<T> {
	items: T[];
	totalCount: number;
	page: number;
	pageSize: number;
	totalPages: number;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
}

/** One printable asset label: the deep link to encode plus the caption printed under it. */
export interface AssetQrCodeItem {
	assetId: number;
	name: string;
	inventoryNumber: string | null;
	qrValue: string;
}

export interface GetAssetsParams {
	name?: string[];
	categoryId?: number[];
	typeId?: number[];
	groupId?: number[];
	siteId?: number[];
	departmentId?: number[];
	areaId?: number[];
	status?: number[];
	available?: number[];
	maintenance?: number[];
	fromLastMaintenanceDate?: string;
	toLastMaintenanceDate?: string;
	fromNextMaintenanceDate?: string;
	toNextMaintenanceDate?: string;
	searchTerm?: string;
	sortBy?: AssetSortField;
	sortOrder?: SortDirection;
}

export interface AssetsRegisterCounts {
	assets: number;
	categories: number;
	groups: number;
	types: number;
}

interface AssetIdentificationRequest {
	name: string;
	serialNumber?: string | null;
	inventoryNumber?: string | null;
	externalAssetId?: string | null;
	barcode?: string | null;
}

interface AssetTypeRequest {
	id: number;
	make?: string | null;
	model?: string | null;
}

interface AssetMeasuresRequest {
	runtimeHoursEnabled: boolean;
	runtimeHoursValue: number;
	odometerEnabled: boolean;
	odometerValue: number;
}

interface AssetCustomParameterRequest {
	/** Present for rows that already exist, so an edit updates them in place. */
	id?: number | null;
	type: string;
	value?: string | null;
	unit?: string | null;
}

interface OwnedAssetOwnershipDetailsRequest {
	purchaseDate?: string | null;
	initialAssetValue?: number | null;
	initialAssetValueCurrency?: number | null;
	depreciationPeriodValue?: number | null;
	depreciationPeriodUnit?: number | null;
	currentBookValue?: number | null;
	currentBookValueCurrency?: number | null;
	warrantyStartDate?: string | null;
	warrantyEndDate?: string | null;
}

interface RentedAssetOwnershipDetailsRequest {
	rentalPartner?: string | null;
	contractStartDate?: string | null;
	contractEndDate?: string | null;
	rentalCost: number;
	currency: number;
	contractReference?: string | null;
}

interface AssetFinancialRequest {
	costCentre?: string | null;
	projectNumber?: string | null;
	fixedAssetNumber?: string | null;
	financialAssetId?: string | null;
	financialAccountNumber?: string | null;
}

export interface CreateAssetRequest {
	assetIdentification: AssetIdentificationRequest;
	assetType?: AssetTypeRequest | null;
	groupId?: number | null;
	siteId?: number | null;
	areaId?: number | null;
	departmentId?: number | null;
	responsibleUserId?: number | null;
	status: number;
	availability: number;
	assetMeasures?: AssetMeasuresRequest | null;
	customParameters?: AssetCustomParameterRequest[] | null;
	ownershipType: number;
	ownedAssetOwnershipDetails?: OwnedAssetOwnershipDetailsRequest | null;
	rentedAssetOwnershipDetails?: RentedAssetOwnershipDetailsRequest | null;
	assetFinancial?: AssetFinancialRequest | null;
	serviceContractAssigned: boolean;
	serviceContractId?: number | null;
	maintenanceRules?: number[] | null;
}

export interface UpdateAssetRequest extends CreateAssetRequest {}

/**
 * Remove undefined values from params object
 * Axios includes undefined values in query string which can cause issues
 */
function cleanParams<T extends object>(params: T): Partial<T> {
	const cleaned: Record<string, unknown> = {};
	for (const key in params) {
		if (Object.prototype.hasOwnProperty.call(params, key)) {
			const value = params[key];
			if (value !== undefined && value !== null && value !== '') {
				if (Array.isArray(value) && value.length === 0) continue;
				cleaned[key] = value;
			}
		}
	}
	return cleaned as Partial<T>;
}

/** Repeats array params as `key=a&key=b`, which is what the ASP.NET query binder expects. */
function serializeListParams(params: Record<string, unknown>): string {
	const parts: string[] = [];
	for (const key in params) {
		const value = params[key];
		if (Array.isArray(value)) {
			value.forEach((item) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`));
		} else {
			parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value as string | number)}`);
		}
	}
	return parts.join('&');
}

export async function getAssets(scope: OrgScope, params: GetAssetsParams = {}): Promise<Asset[]> {
	const response = await apiClient.get<Asset[]>('/api/assets', {
		...scopeHeaders(scope),
		params: cleanParams(params),
		paramsSerializer: serializeListParams,
	});
	return response.data;
}

/**
 * QR label data for every asset the given filters match — the same filters the list itself
 * sends, so the printed sheet covers exactly what the register shows.
 */
export async function getAssetQrCodes(scope: OrgScope, params: GetAssetsParams = {}): Promise<AssetQrCodeItem[]> {
	const response = await apiClient.get<AssetQrCodeItem[]>('/api/assets/qr-codes', {
		...scopeHeaders(scope),
		params: cleanParams(params),
		paramsSerializer: serializeListParams,
	});
	return response.data;
}

/**
 * The asset register as an .xlsx workbook, under the same filters the list itself sends.
 * `assetIds` narrows it to a selection; it can never widen the set the filters allow.
 *
 * The workbook is built server-side so the file matches the import template column for column —
 * a download can be edited and uploaded straight back through the asset import.
 */
export async function exportAssets(scope: OrgScope, params: GetAssetsParams = {}, assetIds?: number[]): Promise<Blob> {
	const response = await apiClient.get('/api/assets/export', {
		...scopeHeaders(scope),
		params: cleanParams({ ...params, assetId: assetIds }),
		paramsSerializer: serializeListParams,
		responseType: 'blob',
	});
	return response.data as Blob;
}

/**
 * Import mode, as the API's `AssetImportMode` enum. Sent and received as numbers because the API
 * serialises enums numerically.
 */
export const AssetImportMode = { CreateOnly: 0, Upsert: 1 } as const;

export type AssetImportMode = (typeof AssetImportMode)[keyof typeof AssetImportMode];

/** One error or warning, anchored to the cell it came from. */
export interface AssetImportMessage {
	sheet: string;
	row?: number | null;
	column?: string | null;
	columnName?: string | null;
	message: string;
}

/** The outcome of a dry run or a commit. An invalid file is a 200 with `isValid: false`. */
export interface AssetImportReport {
	isValid: boolean;
	committed: boolean;
	assetRowsRead: number;
	/** The mode the import ran in — the one asked for, or the one inferred from the file. */
	mode: AssetImportMode;
	/** Rows naming an existing asset, i.e. rows that came from an export. */
	rowsWithAssetId: number;
	assetsToCreate: number;
	assetsToUpdate: number;
	customParametersToCreate: number;
	/** Assets whose QR code is being minted in the background; the assets themselves are committed. */
	qrCodesQueued: number;
	createdLookups: string[];
	ignoredColumns: string[];
	errors: AssetImportMessage[];
	warnings: AssetImportMessage[];
}

/**
 * Parses and checks the workbook without writing anything. Omit `mode` to let the API decide:
 * a file whose rows carry an AssetId is an edited export and runs as an Upsert.
 */
export async function validateAssetImport(
	scope: WriteScope,
	file: File,
	mode?: AssetImportMode,
): Promise<AssetImportReport> {
	return postAssetImport('/api/assets/import/validate', scope, file, { mode });
}

/**
 * Commits the workbook. All-or-nothing: one failed row writes nothing.
 *
 * QR codes are generated by default — an asset without one cannot be scanned in the field. Only a
 * create ever mints one, so an Upsert leaves the QR code an existing asset already has untouched.
 * They are minted in the background: the response returns as soon as the assets are committed, and
 * `qrCodesQueued` says how many are still on their way.
 */
export async function importAssets(
	scope: WriteScope,
	file: File,
	mode?: AssetImportMode,
	generateQrCodes = true,
): Promise<AssetImportReport> {
	return postAssetImport('/api/assets/import', scope, file, { mode, generateQrCodes });
}

async function postAssetImport(
	url: string,
	scope: WriteScope,
	file: File,
	params: { mode?: AssetImportMode; generateQrCodes?: boolean },
): Promise<AssetImportReport> {
	const formData = new FormData();
	formData.append('file', file);

	const response = await apiClient.post<AssetImportReport>(url, formData, {
		params: cleanParams(params),
		headers: {
			'Content-Type': 'multipart/form-data',
			...scopeHeaders(scope).headers,
		},
	});

	return response.data;
}

/** The blank upload workbook, with the Reference sheet filled in for this organisation. */
export async function getAssetImportTemplate(scope: OrgScope): Promise<Blob> {
	const response = await apiClient.get('/api/assets/import/template', {
		...scopeHeaders(scope),
		responseType: 'blob',
	});

	return response.data as Blob;
}

export async function getAssetById(scope: OrgScope, id: string): Promise<AssetDetail> {
	const response = await apiClient.get<AssetDetail>(`/api/assets/${id}`, scopeHeaders(scope));
	return response.data;
}

export async function createAsset(scope: WriteScope, data: CreateAssetRequest): Promise<number> {
	const response = await apiClient.post<number>('/api/Assets', data, scopeHeaders(scope));
	return response.data;
}

export async function updateAsset(scope: WriteScope, id: string, data: UpdateAssetRequest): Promise<void> {
	await apiClient.put(`/api/Assets/${id}`, data, scopeHeaders(scope));
}

export async function deleteAsset(scope: WriteScope, id: number): Promise<void> {
	await apiClient.delete(`/api/assets/${id}`, scopeHeaders(scope));
}

export async function bulkDeleteAssets(scope: WriteScope, ids: number[]): Promise<void> {
	await apiClient.post('/api/assets/bulk-delete', ids, scopeHeaders(scope));
}

export async function deleteAssetImage(scope: WriteScope, assetId: number): Promise<void> {
	await apiClient.delete(`/api/assets/${assetId}/image`, scopeHeaders(scope));
}

export async function deleteAssetDocument(scope: WriteScope, assetId: number, documentId: number): Promise<void> {
	await apiClient.delete(`/api/assets/${assetId}/documents/${documentId}`, scopeHeaders(scope));
}

export async function uploadAssetFiles(
	scope: WriteScope,
	assetId: number,
	image?: File | null,
	documents?: File[] | null,
): Promise<void> {
	const formData = new FormData();

	if (image) {
		formData.append('Image', image);
	}

	if (documents && documents.length > 0) {
		documents.forEach((doc) => {
			formData.append('Documents', doc);
		});
	}

	await apiClient.post(`/api/Assets/${assetId}/upload`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
			...scopeHeaders(scope).headers,
		},
	});
}

export async function getAssetsBaseInfo(
	scope: OrgScope,
	options: { onlyWithoutContract?: boolean } = {},
): Promise<AssetBaseInfoDTO[]> {
	const response = await apiClient.get<AssetBaseInfoDTO[]>('/api/assets/base-info', {
		...scopeHeaders(scope),
		params: options,
	});
	return response.data;
}

export async function getCategoriesBaseInfo(scope: OrgScope): Promise<BaseDTO[]> {
	const response = await apiClient.get<BaseDTO[]>('/api/categories/base-info', scopeHeaders(scope));
	return response.data;
}

export async function getTypesBaseInfo(scope: OrgScope): Promise<BaseDTO[]> {
	const response = await apiClient.get<BaseDTO[]>('/api/types/base-info', scopeHeaders(scope));
	return response.data;
}

export async function getTypesWithCategory(scope: OrgScope): Promise<TypeWithCategoryInfo[]> {
	const response = await apiClient.get<TypeWithCategoryInfo[]>('/api/types/type-with-category', scopeHeaders(scope));
	return response.data;
}

export async function getGroupsBaseInfo(scope: OrgScope): Promise<BaseDTO[]> {
	const response = await apiClient.get<BaseDTO[]>('/api/groups/base-info', scopeHeaders(scope));
	return response.data;
}

export async function getSitesBaseInfo(scope: OrgScope): Promise<BaseDTO[]> {
	const response = await apiClient.get<BaseDTO[]>('/api/sites/base-info', scopeHeaders(scope));
	return response.data;
}

/**
 * Areas belong to a site, so a form that has a site selected passes its id and gets only that
 * site's areas back. Omitting it returns every area in scope.
 */
export async function getAreasBaseInfo(scope: OrgScope, options: { siteId?: number } = {}): Promise<AreaBaseInfoDTO[]> {
	const { siteId } = options;
	const response = await apiClient.get<AreaBaseInfoDTO[]>('/api/areas/base-info', {
		...scopeHeaders(scope),
		params: siteId ? { siteId } : undefined,
	});
	return response.data;
}

export async function getSiteWithAddress(scope: OrgScope, id: number): Promise<SiteWithAddressDTO> {
	const response = await apiClient.get<SiteWithAddressDTO>(`/api/sites/${id}`, scopeHeaders(scope));
	return response.data;
}

/**
 * NOTE: this is NOT the same call as `userService.getUsersBaseInfo`, despite the shared name and
 * endpoint — that one narrows by `siteId`, this one by `departmentId`. They are not
 * interchangeable; check which filter you need before switching imports.
 */
export async function getUsersBaseInfo(scope: OrgScope, options: { departmentId?: number } = {}): Promise<BaseDTO[]> {
	const { departmentId } = options;
	const response = await apiClient.get<BaseDTO[]>('/api/users/base-info', {
		...scopeHeaders(scope),
		params: departmentId ? { departmentId } : {},
	});
	return response.data;
}

/**
 * Tab-badge totals for the assets register.
 *
 * Like the dashboard, these are aggregates over tenant-owned rows, and like the dashboard this
 * took no scope at all — so it never used the fail-open helper and was never forced to convert.
 *
 * 🚨 These are PAGE-level totals and the Organisation filter is PER-TAB, so a GlobalAdmin who
 * filters a tab to one organisation still sees a badge counting every organisation. That mismatch
 * is a product question, not something this signature can settle: the page has no organisation
 * filter of its own for the badge to follow. What the scope parameter fixes is that the breadth is
 * now stated by the caller instead of falling out of an omitted argument.
 */
export async function getAssetsRegisterCounts(scope: OrgScope): Promise<AssetsRegisterCounts> {
	const response = await apiClient.get<AssetsRegisterCounts>('/api/assets-register', scopeHeaders(scope));
	return response.data;
}

export async function createType(
	scope: WriteScope,
	data: { typeName: string; categoryId: number; isVehicle: boolean },
): Promise<number> {
	const response = await apiClient.post<number>('/api/types', data, scopeHeaders(scope));
	return response.data;
}

export default {
	getAssets,
	getAssetQrCodes,
	exportAssets,
	validateAssetImport,
	importAssets,
	getAssetImportTemplate,
	getAssetById,
	createAsset,
	updateAsset,
	deleteAsset,
	bulkDeleteAssets,
	uploadAssetFiles,
	getAssetsBaseInfo,
	getCategoriesBaseInfo,
	getTypesBaseInfo,
	getTypesWithCategory,
	getGroupsBaseInfo,
	getSitesBaseInfo,
	getAreasBaseInfo,
	getSiteWithAddress,
	getUsersBaseInfo,
	getAssetsRegisterCounts,
};
